import {
    RotateCcw,
} from "lucide-preact";
import {
    useCallback,
    useEffect,
    useState,
} from "preact/compat";

import {
    Button,
} from "@mrflark/ui";

type AnalysisStatus = "all" | "analyzed" | "not_analyzed";
type SortField = "confidence" | "analyzed_at" | "analysis_count";

export type AiAnalysisFilters = {
    status: AnalysisStatus;
    modelName: string;
    sortBy: SortField;
    sortOrder: "asc" | "desc";
    minConfidence: number | undefined;
    maxConfidence: number | undefined;
    analyzedAfter: string;
    analyzedBefore: string;
    minAnalysisCount: number | undefined;
    descriptionSearch: string;
    // videos render as <video> in the AI analysis grid and don't have
    // poster thumbnails — they show as gray squares. default-off so
    // the grid stays scannable; the operator can opt-in when triaging
    // video-specific AI analysis. TODO(spl): the AI analysis pipeline
    // currently only runs on images (isImageExtension gate inside the
    // process_media chain); investigate extending it to webm / mp4
    // via per-frame sampling so this checkbox surfaces real signal.
    includeVideos: boolean;
};

export const DEFAULT_FILTERS: AiAnalysisFilters = {
    status: "all",
    modelName: "",
    sortBy: "confidence",
    sortOrder: "desc",
    minConfidence: undefined,
    maxConfidence: undefined,
    analyzedAfter: "",
    analyzedBefore: "",
    minAnalysisCount: undefined,
    descriptionSearch: "",
    includeVideos: false,
};

type AiAnalysisFilterBarProps = {
    filters: AiAnalysisFilters;
    onChange: (filters: AiAnalysisFilters) => void;
    models: string[];
};

export const AiAnalysisFilterBar = ({
    filters,
    onChange,
    models,
}: AiAnalysisFilterBarProps) => {
    const [searchInput, setSearchInput] = useState(filters.descriptionSearch);

    // debounce description search
    useEffect(() => {
        if (searchInput === filters.descriptionSearch) return;
        const timeout = setTimeout(() => {
            onChange({
                ...filters,
                descriptionSearch: searchInput,
            });
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchInput, filters, onChange]);

    const updateFilter = useCallback(<K extends keyof AiAnalysisFilters>(
        key: K,
        value: AiAnalysisFilters[K],
    ) => {
        onChange({
            ...filters,
            [key]: value,
        });
    }, [filters, onChange]);

    const handleReset = (): void => {
        setSearchInput("");
        onChange(DEFAULT_FILTERS);
    };

    const hasActiveFilters = filters.status !== "all" ||
        filters.modelName !== "" ||
        filters.minConfidence !== undefined ||
        filters.maxConfidence !== undefined ||
        filters.analyzedAfter !== "" ||
        filters.analyzedBefore !== "" ||
        filters.minAnalysisCount !== undefined ||
        filters.descriptionSearch !== "";

    return (
        <div className="flex flex-wrap items-end gap-4">
            {/* analysis status toggle */}
            <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                    Status
                </label>
                <div className="flex gap-1">
                    {[
                        {
                            value: "all" as const,
                            label: "All",
                        },
                        {
                            value: "analyzed" as const,
                            label: filters.modelName
                                ? `Analyzed by ${filters.modelName}`
                                : "Analyzed",
                        },
                        {
                            value: "not_analyzed" as const,
                            label: filters.modelName
                                ? `Not analyzed by ${filters.modelName}`
                                : "Not Analyzed",
                        },
                    ].map((option) => (
                        <button
                            type="button"
                            key={option.value}
                            onClick={() => updateFilter("status", option.value)}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                                filters.status === option.value
                                    ? "bg-accent text-white"
                                    : "bg-bg-tertiary text-text-secondary hover:text-text-primary"
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={filters.includeVideos}
                        onChange={() =>
                            updateFilter(
                                "includeVideos",
                                !filters.includeVideos,
                            )}
                        className="rounded border-border"
                    />
                    <span title="Videos render as <video> in this grid and lack poster thumbnails — they appear as gray squares unless poster generation has caught up. AI analysis itself currently runs on images only.">
                        Include videos
                    </span>
                </label>
            </div>

            {/* model dropdown */}
            <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                    Model
                </label>
                <select
                    value={filters.modelName}
                    onChange={(event) =>
                        updateFilter(
                            "modelName",
                            (event.target as HTMLInputElement).value,
                        )}
                    className="input py-1.5 text-sm"
                >
                    <option value="">All Models</option>
                    {models.map((model) => (
                        <option key={model} value={model}>{model}</option>
                    ))}
                </select>
            </div>

            {/* sort dropdown */}
            <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                    Sort By
                </label>
                <select
                    value={filters.sortBy}
                    onChange={(event) =>
                        updateFilter(
                            "sortBy",
                            (event.target as HTMLInputElement)
                                .value as SortField,
                        )}
                    className="input py-1.5 text-sm"
                >
                    <option value="confidence">Confidence</option>
                    <option value="analyzed_at">Date Analyzed</option>
                    <option value="analysis_count">Analysis Count</option>
                </select>
            </div>

            {/* sort order toggle */}
            <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                    Order
                </label>
                <button
                    type="button"
                    onClick={() =>
                        updateFilter(
                            "sortOrder",
                            filters.sortOrder === "asc" ? "desc" : "asc",
                        )}
                    className="px-3 py-1.5 text-xs font-medium rounded bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
                >
                    {filters.sortOrder === "asc" ? "Ascending" : "Descending"}
                </button>
            </div>

            {/* confidence range */}
            <div className="flex items-end gap-2">
                <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                        Min %
                    </label>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        value={filters.minConfidence !== undefined
                            ? Math.round(filters.minConfidence * 100)
                            : ""}
                        onChange={(event) => {
                            const raw =
                                (event.target as HTMLInputElement).value;
                            updateFilter(
                                "minConfidence",
                                raw === "" ? undefined : parseFloat(raw) / 100,
                            );
                        }}
                        className="input py-1.5 text-sm w-20"
                        placeholder="0"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                        Max %
                    </label>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        value={filters.maxConfidence !== undefined
                            ? Math.round(filters.maxConfidence * 100)
                            : ""}
                        onChange={(event) => {
                            const raw =
                                (event.target as HTMLInputElement).value;
                            updateFilter(
                                "maxConfidence",
                                raw === "" ? undefined : parseFloat(raw) / 100,
                            );
                        }}
                        className="input py-1.5 text-sm w-20"
                        placeholder="100"
                    />
                </div>
            </div>

            {/* date range */}
            <div className="flex items-end gap-2">
                <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                        After
                    </label>
                    <input
                        type="date"
                        value={filters.analyzedAfter}
                        onChange={(event) =>
                            updateFilter(
                                "analyzedAfter",
                                (event.target as HTMLInputElement).value,
                            )}
                        className="input py-1.5 text-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                        Before
                    </label>
                    <input
                        type="date"
                        value={filters.analyzedBefore}
                        onChange={(event) =>
                            updateFilter(
                                "analyzedBefore",
                                (event.target as HTMLInputElement).value,
                            )}
                        className="input py-1.5 text-sm"
                    />
                </div>
            </div>

            {/* analysis count filter */}
            <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                    Analyses
                </label>
                <select
                    value={filters.minAnalysisCount ?? "any"}
                    onChange={(event) => {
                        const val = (event.target as HTMLInputElement).value;
                        updateFilter(
                            "minAnalysisCount",
                            val === "any" ? undefined : parseInt(val),
                        );
                    }}
                    className="input py-1.5 text-sm"
                >
                    <option value="any">Any</option>
                    <option value="0">0</option>
                    <option value="1">1</option>
                    <option value="2">2+</option>
                </select>
            </div>

            {/* description search */}
            <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-text-secondary mb-1">
                    Description
                </label>
                <input
                    type="text"
                    value={searchInput}
                    onChange={(event) => setSearchInput(
                        (event.target as HTMLInputElement).value,
                    )}
                    className="input py-1.5 text-sm w-full"
                    placeholder="Search descriptions..."
                />
            </div>

            {/* reset button */}
            {hasActiveFilters && (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleReset}
                >
                    <RotateCcw size={14} className="mr-1" />
                    Reset
                </Button>
            )}
        </div>
    );
};
